interface ErrorBody {
  error?: { code?: string; message?: string };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const headers = new Headers(init?.headers);
  let body = init?.body;
  if (init?.json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(init.json);
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const res = await fetch(path, {
    ...init,
    headers,
    body,
    credentials: 'include',
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = null;
  let parseFailed = false;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      parseFailed = true;
    }
  }

  if (!res.ok) {
    const err = data as ErrorBody | null;
    // A non-JSON body on an error almost always means the response came from
    // the platform gateway, not our API (e.g. an HTML 502/504 timeout page).
    // Dumping raw HTML into the UI is unreadable, so collapse it to a clean,
    // status-aware message rather than slicing the markup.
    const looksLikeHtml = parseFailed && /^\s*<(?:!doctype|html)/i.test(text);
    const fallback = looksLikeHtml
      ? `The server didn't respond properly (HTTP ${res.status}). It may have timed out — please try again.`
      : parseFailed && text
        ? text.slice(0, 200)
        : `Request failed (${res.status})`;
    throw new ApiError(
      res.status,
      err?.error?.code ?? 'request_failed',
      err?.error?.message ?? fallback,
    );
  }

  if (parseFailed) {
    throw new ApiError(res.status, 'invalid_json', `Server returned non-JSON: ${text.slice(0, 200)}`);
  }

  return data as T;
}
